import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, LocateFixed, ShieldAlert } from "lucide-react";

/**
 * Full-page gate for the Driver day page: blocks all content until the
 * browser's location permission is granted. Calls onGranted(coords) once
 * the user shares their position.
 */
export default function LocationGate({ onGranted }) {
  const [status, setStatus] = useState("checking"); // checking|prompt|denied|unsupported
  const [requesting, setRequesting] = useState(false);

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("unsupported");
      return;
    }
    setRequesting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setRequesting(false);
        onGranted({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        setRequesting(false);
        setStatus(err && err.code === 1 ? "denied" : "prompt");
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  }, [onGranted]);

  // Check current permission; auto-request silently when already granted.
  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus("unsupported");
      return undefined;
    }
    let active = true;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((st) => {
        if (!active) return;
        if (st.state === "granted") request();
        else setStatus(st.state); // prompt | denied
        st.onchange = () => {
          if (st.state === "granted") request();
          else setStatus(st.state);
        };
      }).catch(() => { if (active) setStatus("prompt"); });
    } else {
      setStatus("prompt");
    }
    return () => { active = false; };
  }, [request]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="driver-location-gate">
      <div className="max-w-md w-full border border-border rounded-sm p-8 text-center space-y-4">
        {status === "denied" ? (
          <ShieldAlert className="w-12 h-12 text-destructive mx-auto" />
        ) : (
          <MapPin className="w-12 h-12 text-primary mx-auto" />
        )}
        <h1 className="font-display text-2xl font-black tracking-tighter">Location required</h1>
        {status === "checking" && (
          <p className="text-sm text-muted-foreground">Checking location permission…</p>
        )}
        {status === "prompt" && (
          <p className="text-sm text-muted-foreground">
            The driver page needs your GPS to sort stops by best route, give customers live ETAs,
            and let dispatch see where you are. Tap below and choose <strong>Allow</strong>.
          </p>
        )}
        {status === "denied" && (
          <p className="text-sm text-muted-foreground" data-testid="location-gate-denied">
            Location access is <strong className="text-destructive">blocked</strong> for this site.
            Enable it in your browser settings (tap the lock/site icon next to the address bar →
            Permissions → Location → Allow), then try again.
          </p>
        )}
        {status === "unsupported" && (
          <p className="text-sm text-destructive">This device or browser doesn&apos;t support GPS — the driver page can&apos;t be used here.</p>
        )}
        {status !== "unsupported" && (
          <Button onClick={request} disabled={requesting} className="rounded-sm h-12 w-full font-semibold" data-testid="location-gate-enable">
            <LocateFixed className="w-4 h-4 mr-2" />
            {requesting ? "Getting your GPS…" : status === "denied" ? "Try again" : "Enable location"}
          </Button>
        )}
      </div>
    </div>
  );
}
